import React from "react";
import AppleWalletButton from "./AppleWalletButton";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import withErrorBoundary from "./withErrorBoundary";
import LogoBigColor from "@/icons/LogoBigColor";

type SeatData = { zone: string; zoneCode: string; row: string; seat: string };

const DATE_FORMATS = ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD', moment.ISO_8601];

const Field = ({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) => (
    <div className={className}>
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-grey50 dark:text-nightMuted">{label}</div>
        <div className="mt-1 text-sm font-semibold text-grey10 dark:text-white">{children}</div>
    </div>
);

const Notch = ({ className }: { className: string }) => (
    <div className={`absolute h-10 w-10 rounded-full bg-white dark:bg-nightBg ${className}`} />
);

const ConcertTicket = ({ orderHashedId, eventTitle, selectedDate, venue, address, ticketType, price, attendeeName, eventImage, eventId, ticketQr, event, seatData }: { id: string, eventTitle: string, selectedDate: string, venue: string, address: string, ticketType: string, price: string, orderNumber: string, orderHashedId: string, attendeeName: string, eventImage: string, eventId: string, ticketQr: string, ticketNumber: string, event: any, seatData?: SeatData | null }) => {
    const navigate = useNavigate();

    const dateKey = selectedDate && selectedDate !== 'TBD' ? selectedDate.slice(0, 10) : '';
    const time = event?.times?.find((t: any) => t?.startDate?.slice(0, 10) === dateKey) || event?.times?.[0];
    const start = moment(dateKey ? selectedDate : time?.startDate, DATE_FORMATS);
    const end = time?.endDate ? moment(time.endDate, DATE_FORMATS) : null;
    const hasStartTime = !!time?.startDate || (selectedDate || '').includes(' ');

    const dateText = start.isValid() ? start.format('MMM D, YYYY') : 'TBD';
    const timeText = start.isValid() && hasStartTime
        ? `${start.format('h:mm A')}${end?.isValid() ? ` - ${end.format('h:mm A')}` : ''}`
        : '';

    const hasSeat = !!seatData && Object.keys(seatData).length > 0;
    const section = seatData?.zoneCode || seatData?.zone || '-';
    const where = [venue, address].filter(Boolean).join(', ');
    const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${orderHashedId}:${ticketQr}:${eventId}`;

    const seatFields = hasSeat && (
        <Field label="Location">
            <div className="grid grid-cols-[78px_56px_auto] gap-x-0">
                <span className="text-xs font-medium text-grey50 dark:text-nightMuted">Section:</span>
                <span className="text-xs font-medium text-grey50 dark:text-nightMuted">Row:</span>
                <span className="text-xs font-medium text-grey50 dark:text-nightMuted">Seat:</span>
                <span>{section}</span>
                <span>{seatData?.row || '-'}</span>
                <span>{seatData?.seat || '-'}</span>
            </div>
        </Field>
    );

    const ticketField = (
        <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-grey50 dark:text-nightMuted">Ticket</div>
            <div className="mt-1 text-xs text-grey40 dark:text-nightMuted">{attendeeName}</div>
            <div className="text-sm font-semibold text-grey10 dark:text-white">{ticketType}</div>
            {price && <div className="text-sm font-semibold text-primary dark:text-nightAccent">{price}</div>}
        </div>
    );

    const qrBlock = (
        <div className="flex flex-col items-center gap-7 md:justify-self-end">
            <div className="flex h-[132px] w-[132px] items-center justify-center rounded-2xl bg-white p-3 ring-1 ring-black/[0.06]">
                <img src={qrSrc} alt="QR Code" className="w-full h-full" />
            </div>
            <AppleWalletButton hashedId={orderHashedId} qrCode={ticketQr} />
        </div>
    );

    return (
        <div className="relative w-full overflow-hidden rounded-[28px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/10">
            <div className="hidden md:grid grid-cols-[185px_1fr_auto] gap-10 p-6">
                <Notch className="left-[209px] -top-5" />
                <Notch className="left-[209px] -bottom-5" />

                <div className="flex flex-col justify-between gap-6">
                    <button type="button" onClick={() => navigate(`/event/${eventId}`)} className="group block h-[104px] w-[185px] overflow-hidden rounded-2xl">
                        <img src={eventImage} alt={`${eventTitle} Poster`} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]" />
                    </button>
                    <LogoBigColor width={133} height={30} />
                </div>

                <div className="flex flex-col gap-3 min-w-0">
                    <Field label="Event">
                        <span className="text-xl font-bold leading-tight tracking-[-0.02em]">{eventTitle}</span>
                    </Field>
                    <Field label="When">
                        {dateText}
                        {timeText && <><span className="font-normal text-grey40 dark:text-nightMuted"> from </span>{timeText}</>}
                    </Field>
                    <div className="flex gap-10">
                        {ticketField}
                        {seatFields}
                    </div>
                    {where && <Field label="Where">{where}</Field>}
                </div>

                {qrBlock}
            </div>

            <div className="md:hidden flex flex-col gap-4 px-4 pt-8 pb-10">
                <Notch className="left-1/2 -translate-x-1/2 -top-5" />
                <Notch className="left-1/2 -translate-x-1/2 -bottom-5" />

                <div className="flex items-start justify-between gap-3">
                    <LogoBigColor width={102} height={23} />
                    <div className="text-right">
                        {timeText && <div className="text-xs font-bold uppercase tracking-[0.12em] text-primary dark:text-nightAccent">{timeText}</div>}
                        <div className="text-sm font-semibold text-grey10 dark:text-white">{dateText}</div>
                    </div>
                </div>

                <Field label="Event">
                    <button type="button" onClick={() => navigate(`/event/${eventId}`)} className="text-left text-xl font-bold leading-tight tracking-[-0.02em]">
                        {eventTitle}
                    </button>
                </Field>
                {ticketField}
                {seatFields}
                {where && <Field label="Where">{where}</Field>}

                <div className="pt-6">{qrBlock}</div>
            </div>
        </div>
    );
};

export default withErrorBoundary(ConcertTicket);
